import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { Button } from '../ui/button';
import { Pencil, Trash2, Check, AlertTriangle, Mountain, Ruler, Hand, Undo2, X } from 'lucide-react';
import { SiteLayerOverlays } from './SiteMapLayers';
import { useTouchHint } from '../../lib/useTouchHint';
// DC-3 — boundary-confirm path now runs through the EAS 4-stage
// cascade (vertex → in-polygon-grid → buffered-bbox-grid → USGS
// centroid) so small / coverage-edge polygons get the same three
// extra chances `refreshSiteElevation` already has. The legacy
// `fetchPolygonElevations` helper stays in the tree for other
// callers; this drawer simply opts into the richer audit trail.
import { fetchPolygonElevationsCascading } from '../../lib/fetchPolygonElevationsCascading';
import type { ElevationSamplingResult } from '../../services/dataProviders/elevation/cascadingSampler';
import { AddressLabelOverrideControl } from './AddressLabelOverrideControl';

export interface BoundaryResult {
  boundary: { lat: number; lng: number }[];
  areaAcres: number;
  perimeterKm: number;
  centroid: { lat: number; lng: number };
  // EL2: widened to `number | null`. The legacy `elev?.min ?? 0`
  // pattern at every consumer (KMZ import, draw-confirm) clobbered
  // failed lookups to 0, which then propagated into the SED as a
  // false "0 m ASL" reading. Null now flows end-to-end so the SED
  // assembly + render layer can say "Data unavailable" explicitly.
  // Units: metres ASL.
  elevationMin: number | null;
  elevationMax: number | null;
  elevationAvg: number | null;
  /** EL3 — population stdev across sampled points (m ASL), null when
   *  fewer than 2 points came back. */
  elevationStdev?: number | null;
  /** EL3 — number of sample points that returned a usable elevation. */
  elevationPointsSampled?: number;
  /**
   * DC-3 / EAS-4 — stage-qualified provenance string from the 4-stage
   * cascade. Populated whenever the cascade ran (every boundary-
   * confirm post-DC-3); legacy callers leave it undefined. Read by
   * `siteEvidence.ts` to label the siteProfile section's source as
   * "Sampled via in-polygon grid" / "USGS EPQS centroid" / etc.
   */
  sourceQualification?: ElevationSamplingResult['sourceQualification'];
  cascadeSource?: ElevationSamplingResult['source'];
  /**
   * DC-3 / EAS-4 — per-stage audit trail from the cascade. Stamped
   * onto `EvidenceSection.attemptStages` via the same channel
   * `refreshSiteElevation` uses, so the PRE chip tooltip can surface
   * "Tried 4 strategies: vertex 0/4 returned · in-polygon-grid 18/25
   * returned · stopped (sufficient samples)" on boundary-confirm
   * sites as well as on Refresh-elevation sites.
   */
  cascadeStages?: ElevationSamplingResult['stages'];
  /**
   * Multi-Parcel Site Boundary (MP pack, MP2) — when the user drew /
   * imported additional parcels in the boundary editor, this carries
   * the FULL parcel list. The legacy single-polygon `boundary` is the
   * first parcel for back-compat. Single-parcel sites leave this
   * undefined; consumers persist the canonical `siteBoundaries[]` only
   * when set.
   */
  parcels?: import('../../types/siteBoundary').SiteBoundaryParcel[];
}

interface SiteBoundaryDrawerProps {
  center: { lat: number; lng: number };
  onConfirm: (result: BoundaryResult) => void;
  onSkip: () => void;
  existingBoundary?: { lat: number; lng: number }[];
  apiKey: string;
  /**
   * Read-only layer context — when the wizard already imported a KMZ, the layers
   * are drawn behind the polygon at low opacity so the user can sanity-check
   * boundary placement against constraints + infrastructure.
   */
  existingLayers?: import('../../types/site').SiteLayer[];
  /** Optional display-label override (existing sites). */
  addressLabel?: string;
  canonicalAddress?: string;
  onSaveLabel?: (label: string | undefined) => Promise<void>;
}

export const SiteBoundaryDrawer: React.FC<SiteBoundaryDrawerProps> = ({
  center,
  onConfirm,
  onSkip,
  existingBoundary,
  apiKey,
  existingLayers,
  addressLabel,
  canonicalAddress,
  onSaveLabel,
}) => {
  const map = useMap('boundary-map');
  const [areaAcres, setAreaAcres] = useState<number>(0);
  const [perimeterKm, setPerimeterKm] = useState<number>(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasPolygon, setHasPolygon] = useState(false);
  // EL3: carry stdev + pointsSampled through so the confirm payload
  // can persist them onto the SED. The metrics card still renders
  // avg + min/max only — stdev/pointsSampled live in the SED so AI
  // prompts + downstream reports can flag a degraded sample.
  //
  // DC-3 — also carry the cascade's stage-qualified source + per-
  // stage audit trail so the confirm payload can persist both onto
  // `siteElevationRange`. The siteEvidence producer then stamps
  // `attemptStages` on the siteProfile section (same channel the
  // on-demand `refreshSiteElevation` path uses).
  const [elevations, setElevations] = useState<{
    min: number;
    max: number;
    avg: number;
    stdev: number | null;
    pointsSampled: number;
    sourceQualification?: ElevationSamplingResult['sourceQualification'];
    cascadeSource?: ElevationSamplingResult['source'];
    cascadeStages?: ElevationSamplingResult['stages'];
  } | null>(null);
  // DC-3 — when every cascade stage returned empty we still want to
  // persist the per-stage audit trail (so the PRE chip can say
  // "Tried 4 strategies, all empty"). Stored separately because
  // `elevations` collapses to `null` on the empty branch.
  const [emptyCascadeStages, setEmptyCascadeStages] = useState<ElevationSamplingResult['stages'] | null>(null);
  const [loadingElevation, setLoadingElevation] = useState(false);

  // Migration (maps-migration): the deprecated Maps drawing-library
  // manager was removed from Maps JS v3.65. We now capture the polygon
  // manually with click-to-add-vertex on the parent <Map>, mirroring the
  // SiteLayerDrawOverlay pattern (a live preview google.maps.Polygon +
  // a map "click" listener appending vertices, finished on double-click,
  // first-vertex close, or the explicit Finish control). The resulting
  // editable polygon + BoundaryResult payload are identical to the prior
  // polygon-complete path.
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  // Vertices placed while actively drawing a NEW boundary. Drives the
  // live preview polygon; consumed by `finishDrawing`.
  const [drawVertices, setDrawVertices] = useState<{ lat: number; lng: number }[]>([]);
  const drawVerticesRef = useRef<{ lat: number; lng: number }[]>([]);
  const previewRef = useRef<google.maps.Polygon | null>(null);
  const drawListenersRef = useRef<google.maps.MapsEventListener[]>([]);
  // `isDrawing` is the React state mirror; `isDrawingRef` lets the map
  // click/dblclick listeners (registered once) read the live mode
  // without re-subscribing on every vertex.
  const isDrawingRef = useRef(false);

  const updateMetrics = useCallback((poly: google.maps.Polygon) => {
    if (!google.maps.geometry) return;
    const path = poly.getPath();
    const areaSqM = google.maps.geometry.spherical.computeArea(path);
    setAreaAcres(Math.round(areaSqM / 4046.86));
    const perimM = google.maps.geometry.spherical.computeLength(path);
    setPerimeterKm(parseFloat((perimM / 1000).toFixed(2)));
  }, []);

  const fetchElevations = useCallback(async (poly: google.maps.Polygon) => {
    const path = poly.getPath();
    const locations: { lat: number; lng: number }[] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      locations.push({ lat: p.lat(), lng: p.lng() });
    }
    if (locations.length < 3) return;

    setLoadingElevation(true);
    try {
      // DC-3 — route through the EAS 4-stage cascade: vertex →
      // in-polygon-grid → buffered-bbox-grid → USGS centroid. Stage 1
      // (vertex) is the same single-shot sampling the legacy helper
      // ran, so polygons that already returned data keep returning
      // data on the same stage; small / coverage-edge polygons get
      // three more chances before falling through to the centroid.
      //
      // Returns the legacy stats envelope augmented with cascadeSource
      // + cascadeStages so the confirm payload can persist the audit
      // trail (read by the PRE chip + the EvidenceSection
      // attemptStages tooltip).
      const result = await fetchPolygonElevationsCascading(locations);
      if (result.kind === 'success') {
        setElevations({
          min: result.stats.min,
          max: result.stats.max,
          avg: result.stats.avg,
          stdev: result.stats.stdev,
          pointsSampled: result.stats.pointsSampled,
          ...(result.stats.sourceQualification ? { sourceQualification: result.stats.sourceQualification } : {}),
          cascadeSource: result.stats.source,
          cascadeStages: result.stats.stages,
        });
        setEmptyCascadeStages(null);
      } else {
        // Every cascade stage came back empty. Keep `elevations`
        // null so the metrics card stays at "—", but capture the
        // per-stage audit trail so the confirm payload can carry
        // it forward to the PRE chip ("Tried 4 strategies, all
        // returned 0 / N").
        setElevations(null);
        setEmptyCascadeStages(result.meta.stages);
      }
    } finally {
      setLoadingElevation(false);
    }
  }, []);

  const clearListeners = useCallback(() => {
    for (const l of listenersRef.current) google.maps.event.removeListener(l);
    listenersRef.current = [];
  }, []);

  const attachPathListeners = useCallback((poly: google.maps.Polygon) => {
    const handler = () => { updateMetrics(poly); fetchElevations(poly); };
    listenersRef.current.push(
      google.maps.event.addListener(poly.getPath(), 'set_at', handler),
      google.maps.event.addListener(poly.getPath(), 'insert_at', handler),
    );
  }, [updateMetrics, fetchElevations]);

  const clearPreview = useCallback(() => {
    previewRef.current?.setMap(null);
    previewRef.current = null;
  }, []);

  const removePolygon = useCallback(() => {
    clearListeners();
    polygonRef.current?.setMap(null);
    polygonRef.current = null;
    setHasPolygon(false);
    setAreaAcres(0);
    setPerimeterKm(0);
    setElevations(null);
    // DC-3 — reset the cascade audit trail when the polygon is
    // cleared so the next polygon's empty-cascade branch starts
    // fresh (stale stages from a prior polygon would otherwise
    // mislead the PRE chip on the next confirm).
    setEmptyCascadeStages(null);
  }, [clearListeners]);

  // Finalise the in-progress vertices into a real editable polygon —
  // the exact replacement for the old polygon-complete path: build the
  // same editable/draggable google.maps.Polygon, wire metrics +
  // elevation + path listeners, exit drawing mode.
  const finishDrawing = useCallback((verts: { lat: number; lng: number }[]) => {
    if (!map || verts.length < 3) return;
    removePolygon();
    const poly = new google.maps.Polygon({
      paths: verts,
      strokeColor: '#D97706', strokeWeight: 2,
      fillColor: '#D97706', fillOpacity: 0.15,
      editable: true, draggable: true, map,
    });
    polygonRef.current = poly;
    setHasPolygon(true);
    updateMetrics(poly);
    fetchElevations(poly);
    attachPathListeners(poly);
    // Exit drawing mode + tear down the preview.
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
  }, [map, removePolygon, updateMetrics, fetchElevations, attachPathListeners, clearPreview]);

  // Seed an existing boundary + register the persistent map listeners
  // that drive click-to-add-vertex drawing (registered once per map).
  useEffect(() => {
    if (!map) return;

    if (existingBoundary && existingBoundary.length > 0 && !polygonRef.current) {
      const poly = new google.maps.Polygon({
        paths: existingBoundary,
        strokeColor: '#D97706', strokeWeight: 2,
        fillColor: '#D97706', fillOpacity: 0.15,
        editable: true, draggable: true, map,
      });
      polygonRef.current = poly;
      setHasPolygon(true);
      updateMetrics(poly);
      fetchElevations(poly);
      attachPathListeners(poly);
    }

    // Manual polygon capture (replaces the deprecated Maps drawing-library
    // manager, removed in Maps JS v3.65). Mirrors SiteLayerDrawOverlay: a
    // single 'click' listener appends a vertex; 'dblclick' or clicking near
    // the first vertex closes the polygon. The listeners read `isDrawingRef`
    // so they stay registered for the map's lifetime (no re-subscribe on
    // every vertex).
    const onClick = (e: google.maps.MapMouseEvent) => {
      if (!isDrawingRef.current) return;
      const latLng = e.latLng;
      if (!latLng) return;
      const point = { lat: latLng.lat(), lng: latLng.lng() };

      // Click on (≈) the first vertex closes the polygon (touch-first UX
      // documented in TouchHintOverlay).
      const verts = drawVerticesRef.current;
      if (verts.length >= 3 && google.maps.geometry) {
        const first = new google.maps.LatLng(verts[0].lat, verts[0].lng);
        const here = new google.maps.LatLng(point.lat, point.lng);
        const metres = google.maps.geometry.spherical.computeDistanceBetween(first, here);
        // ~8 px at the wizard's default zoom 15 (~4.8 m/px) ≈ 40 m.
        if (metres < 40) {
          finishDrawing(verts);
          return;
        }
      }

      const next = [...verts, point];
      drawVerticesRef.current = next;
      setDrawVertices(next);
    };

    const onDblClick = (e: google.maps.MapMouseEvent) => {
      if (!isDrawingRef.current) return;
      // Google fires a single 'click' immediately before 'dblclick'; that
      // click already added the final vertex, so close on whatever we have.
      const verts = drawVerticesRef.current;
      if (verts.length >= 3) {
        if (e.stop) e.stop();
        finishDrawing(verts);
      }
    };

    const clickListener = map.addListener('click', onClick);
    const dblListener = map.addListener('dblclick', onDblClick);
    drawListenersRef.current = [clickListener, dblListener];

    return () => {
      for (const l of drawListenersRef.current) google.maps.event.removeListener(l);
      drawListenersRef.current = [];
      clearListeners();
      clearPreview();
      polygonRef.current?.setMap(null);
      polygonRef.current = null;
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live preview polygon while placing vertices (mirrors the
  // SiteLayerDrawOverlay preview overlay).
  useEffect(() => {
    if (!map || !isDrawing || drawVertices.length === 0) {
      clearPreview();
      return;
    }
    clearPreview();
    previewRef.current = new google.maps.Polygon({
      paths: drawVertices,
      strokeColor: '#D97706', strokeWeight: 2,
      fillColor: '#D97706', fillOpacity: 0.2,
      map,
    });
    return () => clearPreview();
  }, [map, isDrawing, drawVertices, clearPreview]);

  const startDrawing = () => {
    removePolygon();
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
    isDrawingRef.current = true;
    setIsDrawing(true);
  };

  const undoLastVertex = () => {
    const next = drawVerticesRef.current.slice(0, -1);
    drawVerticesRef.current = next;
    setDrawVertices(next);
  };

  const cancelDrawing = () => {
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawVerticesRef.current = [];
    setDrawVertices([]);
    clearPreview();
  };

  const handleConfirm = () => {
    const poly = polygonRef.current;
    if (!poly) return;
    const path = poly.getPath();
    const boundary: { lat: number; lng: number }[] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      boundary.push({ lat: p.lat(), lng: p.lng() });
    }
    const centroid = {
      lat: boundary.reduce((s, p) => s + p.lat, 0) / boundary.length,
      lng: boundary.reduce((s, p) => s + p.lng, 0) / boundary.length,
    };
    // EL2: propagate null when the elevation sample failed; the
    // downstream SED assembly treats null as "unavailable" rather than
    // synthesising a 0 m reading. The metrics card already renders
    // "—" via the loadingElevation/elevations branch above.
    //
    // DC-3 — also propagate the cascade audit trail. On the success
    // branch `elevations.cascadeStages` carries every stage that
    // executed; on the all-empty branch `emptyCascadeStages` carries
    // the four no-data attempts so the PRE chip can still surface the
    // strategies the platform tried.
    const cascadeStages = elevations?.cascadeStages ?? emptyCascadeStages ?? undefined;
    onConfirm({
      boundary,
      areaAcres,
      perimeterKm,
      centroid,
      elevationMin: elevations?.min ?? null,
      elevationMax: elevations?.max ?? null,
      elevationAvg: elevations?.avg ?? null,
      elevationStdev: elevations?.stdev ?? null,
      elevationPointsSampled: elevations?.pointsSampled,
      ...(elevations?.sourceQualification ? { sourceQualification: elevations.sourceQualification } : {}),
      ...(elevations?.cascadeSource ? { cascadeSource: elevations.cascadeSource } : {}),
      ...(cascadeStages ? { cascadeStages } : {}),
    });
  };

  const areaWarning = areaAcres > 0 && (areaAcres < 10 || areaAcres > 50000);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="type-meta font-bold text-text-secondary">
          Step 2 — Draw Site Boundary
        </span>
      </div>

      <div className="h-[50vh] min-h-[300px] border border-border relative site-boundary-map rounded-lg overflow-hidden">
        <Map
          id="boundary-map"
          mapId="bf51a910020fa25a"
          defaultCenter={center}
          defaultZoom={15}
          mapTypeId="hybrid"
          gestureHandling="greedy"
        >
          {existingLayers && existingLayers.length > 0 && (
            // Dim everything to 0.4 stroke / 0.1 fill — drawn boundary stays visually dominant.
            <SiteLayerOverlays layers={existingLayers} strokeOpacity={0.4} fillOpacity={0.1} strokeWeight={1.5} />
          )}
        </Map>
        <TouchHintOverlay />
      </div>

      {/* Metrics */}
      {hasPolygon && (
        <div className="grid grid-cols-3 gap-2 p-2 border border-border-subtle bg-state-hover rounded-md">
          <div className="text-center">
            <div className="text-[11px] text-text-muted uppercase tracking-widest">Area</div>
            <div className="text-[11px] font-mono font-bold text-text-primary">{areaAcres} ac</div>
            <div className="text-[10px] text-text-muted">{Math.round((areaAcres * 4046.86) / 10000)} ha</div>
          </div>
          <div className="text-center">
            <div className="text-[11px] text-text-muted uppercase tracking-widest">Perimeter</div>
            <div className="text-[11px] font-mono font-bold text-text-primary">{perimeterKm} km</div>
          </div>
          <div className="text-center">
            <div className="text-[11px] text-text-muted uppercase tracking-widest">Elevation</div>
            {loadingElevation ? (
              <div className="text-[11px] text-text-muted">Loading...</div>
            ) : elevations ? (
              <>
                <div className="text-[11px] font-mono font-bold text-text-primary">{elevations.avg}m</div>
                <div className="text-[10px] text-text-muted">{elevations.min}–{elevations.max}m</div>
              </>
            ) : (
              <div className="text-[11px] text-text-muted">—</div>
            )}
          </div>
        </div>
      )}

      {areaWarning && (
        <div className="flex items-center gap-2 type-body text-warning uppercase tracking-widest">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {areaAcres < 10 ? 'Very small site (< 10 acres).' : 'Very large site (> 50,000 acres).'}
        </div>
      )}

      <div className="flex items-center gap-2">
        {isDrawing ? (
          <>
            <Button
              variant="default"
              size="sm"
              className="h-8 rounded-md text-[11px] uppercase tracking-widest"
              onClick={() => finishDrawing(drawVertices)}
              disabled={drawVertices.length < 3}
              data-testid="boundary-draw-finish"
            >
              <Check className="w-3 h-3 mr-2" /> Finish ({drawVertices.length} pts)
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-md text-[11px] uppercase tracking-widest"
              onClick={undoLastVertex}
              disabled={drawVertices.length === 0}
              data-testid="boundary-draw-undo"
            >
              <Undo2 className="w-3 h-3 mr-2" /> Undo
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-md text-[11px] uppercase tracking-widest"
              onClick={cancelDrawing}
              data-testid="boundary-draw-cancel"
            >
              <X className="w-3 h-3 mr-2" /> Cancel
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" className="h-8 rounded-md text-[11px] uppercase tracking-widest" onClick={startDrawing}>
            <Pencil className="w-3 h-3 mr-2" /> {hasPolygon ? 'Redraw' : 'Draw Boundary'}
          </Button>
        )}
        {hasPolygon && !isDrawing && (
          <Button variant="outline" size="sm" className="h-8 rounded-md text-[11px] uppercase tracking-widest text-danger" onClick={() => { removePolygon(); cancelDrawing(); }}>
            <Trash2 className="w-3 h-3 mr-2" /> Clear
          </Button>
        )}
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-8 rounded-md text-[11px] uppercase tracking-widest" onClick={onSkip}>
          Skip
        </Button>
        <Button variant="default" size="sm" className="h-8 rounded-md text-[11px] uppercase tracking-widest" onClick={handleConfirm} disabled={!hasPolygon || loadingElevation}>
          <Check className="w-3 h-3 mr-2" /> Confirm Boundary
        </Button>
      </div>

      {onSaveLabel && canonicalAddress !== undefined && (
        <AddressLabelOverrideControl
          variant="inline"
          canonicalAddress={canonicalAddress}
          addressLabel={addressLabel}
          onSave={onSaveLabel}
          testIdPrefix="boundary-address-label"
        />
      )}

      <p className="text-[11px] text-text-muted uppercase tracking-widest">
        Click points on the satellite map to draw the site boundary. Double-click to complete. Drag vertices to adjust. Elevation is sampled at each vertex.
      </p>
    </div>
  );
};

/**
 * Touch-target hint shown the first time a user opens the drawer on a coarse-pointer
 * device (touch screen). The drawing manager's UI is mouse-first; this overlay lets
 * the user know that taps add vertices and the first vertex closes the polygon.
 * Persists dismissal so it shows once per browser, not once per session.
 */
function TouchHintOverlay() {
  const { show, dismiss } = useTouchHint('da-boundary-touch-hint-dismissed');
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={dismiss}
      data-testid="boundary-touch-hint"
      aria-label="Dismiss boundary drawing tip"
      className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm cursor-pointer"
    >
      <div className="max-w-xs px-6 py-5 border border-border bg-background space-y-3 text-center rounded-lg">
        <Hand className="w-8 h-8 text-text-secondary mx-auto" />
        <div className="text-sm font-bold uppercase tracking-widest text-text-primary">Tap to draw</div>
        <p className="text-xs text-text-secondary leading-relaxed">
          Tap to add a vertex. Tap your first vertex to close the polygon.
        </p>
        <div className="text-[11px] uppercase tracking-widest text-text-muted">Tap anywhere to dismiss</div>
      </div>
    </button>
  );
}
